<template>
  <section class="container-fluid my-4">
    <div v-if="flow" class="weekly-calendar">

      <!-- Cabeçalho -->
      <h5 class="text-center title-flow">
        Agenda de Atividades Diárias – Semana
        {{ formatDate(flow.start_date) }} a {{ formatDate(flow.end_date) }}
      </h5>

      <!-- Modal: Adicionar Tarefa -->
      <div v-if="showModal" class="modal-backdrop" @click.self="closeModal">
        <div class="modal-box">
          <h6><strong>Adicionar Tarefa</strong> — {{ weekDayNames[selectedDay - 1] }}</h6>
          <div class="form-group mt-2">
            <label>Tarefa</label>
            <select class="form-control" v-model="form.task_id">
              <option :value="null" disabled>Selecione...</option>
              <option v-for="t in tasks" :key="t.id" :value="t.id">{{ t.name }}</option>
            </select>
            <div class="text-right mt-1">
              <a href="#" class="small text-primary" @click.prevent="showInlineTask = !showInlineTask">
                <font-awesome-icon icon="fa-solid fa-plus" />
                {{ showInlineTask ? 'Cancelar nova tarefa' : 'Criar nova tarefa' }}
              </a>
            </div>
          </div>

          <!-- Formulário rápido: nova tarefa -->
          <div v-if="showInlineTask" class="inline-task-form p-2 mb-2">
            <p class="small font-weight-bold mb-2">Nova tarefa</p>
            <div class="form-group mb-1">
              <input
                type="text"
                class="form-control form-control-sm"
                placeholder="Nome da tarefa *"
                v-model="inlineTask.name"
              />
            </div>
            <div class="form-group mb-1">
              <select class="form-control form-control-sm" v-model="inlineTask.task_type_id">
                <option :value="null">Tipo (opcional)</option>
                <option v-for="tt in taskTypes" :key="tt.id" :value="tt.id">{{ tt.name }}</option>
              </select>
            </div>
            <div class="text-right">
              <button
                class="btn btn-primary btn-sm"
                @click="createInlineTask"
                :disabled="savingInlineTask"
              >{{ savingInlineTask ? 'Salvando...' : 'Salvar tarefa' }}</button>
            </div>
          </div>

          <div class="form-group mt-2">
            <label>Hora início</label>
            <input type="time" class="form-control" v-model="form.start_time" />
          </div>
          <div class="form-group mt-2">
            <label>Hora fim (opcional)</label>
            <input type="time" class="form-control" v-model="form.end_time" />
          </div>
          <div class="text-center mt-3">
            <button class="btn btn-success btn-sm mx-1" @click="submitAddTask">Salvar</button>
            <button class="btn btn-secondary btn-sm mx-1" @click="closeModal">Cancelar</button>
          </div>
        </div>
      </div>

      <!-- Grade semanal -->
      <div class="table-responsive">
        <table class="table table-bordered weekly-table">
          <thead>
            <tr>
              <th style="width:80px"></th>
              <th
                v-for="(day, idx) in weekDayNames"
                :key="idx"
                :class="{ 'day-today': isTodayColumn(idx + 1) }"
              >{{ day }}</th>
            </tr>
            <tr>
              <td><strong>HORA</strong></td>
              <td v-for="idx in 7" :key="idx"><strong>HORA</strong></td>
            </tr>
          </thead>
          <tbody>
            <tr v-for="rowIdx in maxRows" :key="rowIdx">
              <td class="text-muted small">{{ rowIdx }}</td>
              <td v-for="dayId in 7" :key="dayId" class="task-cell">
                <template v-if="tasksByDay[dayId] && tasksByDay[dayId][rowIdx - 1]">
                  <div class="task-row">
                    <span class="task-time text-primary">{{ formatTime(tasksByDay[dayId][rowIdx - 1].start_time) }}</span>
                    <span class="task-name" :title="tasksByDay[dayId][rowIdx - 1].task.name">
                      {{ tasksByDay[dayId][rowIdx - 1].task.name }}
                    </span>
                    <span
                      v-if="tasksByDay[dayId][rowIdx - 1].status === 'pending'"
                      class="text-warning"
                      style="font-size:0.65rem; cursor:pointer"
                      @click="openCompleteModal(tasksByDay[dayId][rowIdx - 1])"
                      title="Clique para concluir"
                    ><font-awesome-icon icon="fa-solid fa-hourglass-half" /></span>
                    <span
                      v-else
                      class="text-success"
                      style="font-size:0.65rem"
                      title="Concluída"
                    ><font-awesome-icon icon="fa-solid fa-check" /></span>
                    <button
                      class="btn-remove"
                      @click="deleteFlowTask(tasksByDay[dayId][rowIdx - 1].id)"
                      title="Remover"
                    ><font-awesome-icon icon="fa-solid fa-x" /></button>
                  </div>
                </template>
              </td>
            </tr>
            <!-- Linha de totais -->
            <tr class="total-row">
              <td><strong>TOTAL</strong></td>
              <td v-for="dayId in 7" :key="dayId" class="text-center">
                <span :class="dailyTotal(dayId) === '00:00' ? 'text-danger' : ''">
                  {{ dailyTotal(dayId) }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Botões + Adicionar Tarefa por dia -->
      <div class="d-flex flex-wrap mt-2" style="gap:8px">
        <button
          v-for="dayId in [1,2,3,4,5,6,7]"
          :key="dayId"
          class="btn btn-sm btn-primary"
          @click="openModal(dayId)"
        >+ Adicionar Tarefa ({{ weekDayNamesShort[dayId - 1] }})</button>
      </div>

      <!-- Total semanal -->
      <div class="d-flex justify-content-between align-items-center mt-3">
        <strong>Tempo total na semana: {{ weeklyTotal }}</strong>
        <button class="btn btn-sm btn-danger" @click="exportPdf" :disabled="exporting">
          <font-awesome-icon icon="fa-solid fa-file-pdf" />
          {{ exporting ? 'Gerando PDF...' : 'Exportar PDF' }}
        </button>
      </div>

      <!-- Modal concluir tarefa -->
      <div v-if="showCompleteModal" class="modal-backdrop" @click.self="closeCompleteModal">
        <div class="modal-box">
          <h6><strong>Concluir Tarefa</strong></h6>
          <p>{{ completingTask && completingTask.task.name }}</p>
          <div class="form-group mt-2">
            <label>Hora fim</label>
            <input type="time" class="form-control" v-model="completeForm.end_time" />
          </div>
          <div class="text-center mt-3">
            <button class="btn btn-success btn-sm mx-1" @click="submitComplete">Salvar</button>
            <button class="btn btn-secondary btn-sm mx-1" @click="closeCompleteModal">Cancelar</button>
          </div>
        </div>
      </div>

    </div>

    <div v-else class="text-center py-5">
      <span class="text-muted">Carregando fluxo semanal...</span>
    </div>

    <button class="btn btn-sm btn-secondary mt-3" @click="$router.go(-1)">VOLTAR</button>
  </section>
</template>

<script>
import api from "@/services";

export default {
  name: "WeeklyFlowShow",
  props: ["id"],
  data() {
    return {
      flow: null,
      tasks: [],
      taskTypes: [],
      weekDayNames: ["Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado", "Domingo"],
      weekDayNamesShort: ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"],
      showModal: false,
      selectedDay: null,
      form: { task_id: null, start_time: "", end_time: "" },
      showCompleteModal: false,
      completingTask: null,
      completeForm: { end_time: "" },
      exporting: false,
      showInlineTask: false,
      inlineTask: { name: "", task_type_id: null },
      savingInlineTask: false,
    };
  },
  computed: {
    tasksByDay() {
      const map = {};
      for (let d = 1; d <= 7; d++) map[d] = [];
      if (this.flow && this.flow.flow_tasks) {
        this.flow.flow_tasks.forEach((ft) => {
          map[ft.week_day_id].push(ft);
        });
      }
      return map;
    },
    maxRows() {
      let max = 0;
      for (let d = 1; d <= 7; d++) {
        if (this.tasksByDay[d] && this.tasksByDay[d].length > max) {
          max = this.tasksByDay[d].length;
        }
      }
      return Math.max(max, 1);
    },
    weeklyTotal() {
      let total = 0;
      if (this.flow && this.flow.flow_tasks) {
        this.flow.flow_tasks.forEach((ft) => {
          if (ft.status === "completed" && ft.duration) total += ft.duration;
        });
      }
      return this.minutesToHHMM(total);
    },
  },
  methods: {
    formatDate(dateStr) {
      if (!dateStr) return "";
      const [y, m, d] = dateStr.substring(0, 10).split("-");
      return `${d}/${m}/${y}`;
    },
    formatTime(datetimeStr) {
      if (!datetimeStr) return "";
      return datetimeStr.substring(11, 16);
    },
    isTodayColumn(weekDayId) {
      if (!this.flow) return false;
      const today = new Date().toISOString().substring(0, 10);
      const taskDate = this.getDayDate(weekDayId);
      return taskDate === today;
    },
    getDayDate(weekDayId) {
      if (!this.flow) return null;
      const start = new Date(this.flow.start_date);
      start.setDate(start.getDate() + weekDayId - 1);
      return start.toISOString().substring(0, 10);
    },
    dailyTotal(dayId) {
      const tasks = this.tasksByDay[dayId] || [];
      const total = tasks
        .filter((t) => t.status === "completed" && t.duration)
        .reduce((acc, t) => acc + t.duration, 0);
      return this.minutesToHHMM(total);
    },
    minutesToHHMM(minutes) {
      if (!minutes) return "00:00";
      const h = Math.floor(minutes / 60);
      const m = minutes % 60;
      return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
    },
    openModal(dayId) {
      this.selectedDay = dayId;
      this.form = { task_id: null, start_time: "", end_time: "" };
      this.showInlineTask = false;
      this.inlineTask = { name: "", task_type_id: null };
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
      this.showInlineTask = false;
    },
    openCompleteModal(flowTask) {
      this.completingTask = flowTask;
      this.completeForm = { end_time: "" };
      this.showCompleteModal = true;
    },
    closeCompleteModal() {
      this.showCompleteModal = false;
      this.completingTask = null;
    },
    async submitAddTask() {
      if (!this.form.task_id || !this.form.start_time) {
        Toast.fire("Tarefa e hora início são obrigatórios.", "", "warning");
        return;
      }
      const payload = {
        task_id: this.form.task_id,
        week_day_id: this.selectedDay,
        start_time: this.form.start_time.length === 5 ? this.form.start_time + ":00" : this.form.start_time,
      };
      if (this.form.end_time) {
        payload.end_time = this.form.end_time.length === 5 ? this.form.end_time + ":00" : this.form.end_time;
      }
      try {
        const { data } = await api.post(`/flows/${this.id}/tasks`, payload);
        Toast.fire(data.message, "", "success");
        this.closeModal();
        await this.loadFlow();
      } catch (error) {
        const errors = error.response?.data?.errors || {};
        const msg = Object.values(errors).flat().join(" ") || error.response?.data?.message || "Erro ao adicionar tarefa.";
        Toast.fire(msg, "", "error");
      }
    },
    async submitComplete() {
      if (!this.completeForm.end_time) {
        Toast.fire("Informe a hora fim.", "", "warning");
        return;
      }
      const endTime = this.completeForm.end_time.length === 5
        ? this.completeForm.end_time + ":00"
        : this.completeForm.end_time;
      try {
        const { data } = await api.put(`/flow-tasks/${this.completingTask.id}`, { end_time: endTime });
        Toast.fire(data.message, "", "success");
        this.closeCompleteModal();
        await this.loadFlow();
      } catch (error) {
        const errors = error.response?.data?.errors || {};
        const msg = Object.values(errors).flat().join(" ") || "Erro ao concluir tarefa.";
        Toast.fire(msg, "", "error");
      }
    },
    async deleteFlowTask(flowTaskId) {
      const result = await this.$swal({
        title: "Remover execução?",
        type: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        confirmButtonText: "Sim, remover",
        cancelButtonText: "Cancelar",
      });
      if (!result.value) return;
      try {
        await api.delete(`/flow-tasks/${flowTaskId}`);
        Toast.fire("Tarefa removida.", "", "success");
        await this.loadFlow();
      } catch (error) {
        Toast.fire(error.response?.data?.message || "Erro ao remover.", "", "error");
      }
    },
    async loadFlow() {
      const { data } = await api.get(`/flows/${this.id}`);
      this.flow = data;
    },
    async loadTasks() {
      const { data } = await api.get("/tasks?perPage=200");
      this.tasks = data.data;
    },
    async loadTaskTypes() {
      const { data } = await api.get("/task-types?perPage=100");
      this.taskTypes = data.data;
    },
    async createInlineTask() {
      if (!this.inlineTask.name.trim()) {
        Toast.fire("Informe o nome da tarefa.", "", "warning");
        return;
      }
      this.savingInlineTask = true;
      try {
        const payload = { name: this.inlineTask.name.trim() };
        if (this.inlineTask.task_type_id) payload.task_type_id = this.inlineTask.task_type_id;
        const { data } = await api.post("/tasks", payload);
        await this.loadTasks();
        this.form.task_id = data.data ? data.data.id : data.id;
        this.showInlineTask = false;
        this.inlineTask = { name: "", task_type_id: null };
        Toast.fire("Tarefa criada e selecionada!", "", "success");
      } catch (error) {
        const errors = error.response?.data?.errors || {};
        const msg = Object.values(errors).flat().join(" ") || error.response?.data?.message || "Erro ao criar tarefa.";
        Toast.fire(msg, "", "error");
      } finally {
        this.savingInlineTask = false;
      }
    },
    async exportPdf() {
      this.exporting = true;
      try {
        const response = await api.get(`/exports/weekly-flow/${this.id}`, {
          responseType: "blob",
        });
        const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
        const link = document.createElement("a");
        const startDate = this.flow.start_date.substring(0, 10).replace(/-/g, "");
        link.href = url;
        link.setAttribute("download", `agenda-semanal_${startDate}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        Toast.fire("Erro ao gerar o PDF.", "", "error");
      } finally {
        this.exporting = false;
      }
    },
  },
  async mounted() {
    await Promise.all([this.loadFlow(), this.loadTasks(), this.loadTaskTypes()]);
  },
};
</script>

<style scoped>
.title-flow {
  background: #ebf0de;
  color: #fff;
  margin-bottom: 0px;
  padding: 12px 0;
  color: #78913d;
}
.weekly-calendar {
  font-size: 0.875rem;
}
.weekly-table th {
  text-align: center;
  background: #78913d;
}
.weekly-table td {
  vertical-align: middle;
  padding: 4px 6px;
}
.day-today {
  background-color: #cfe2ff !important;
}
.task-cell {
  min-width: 130px;
}
.task-row {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: nowrap;
}
.task-time {
  font-weight: 600;
  white-space: nowrap;
  min-width: 62px;
}
.task-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 180px;
}
.btn-remove {
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 0.85rem;
  color: #dc3545;
  padding: 0 2px;
}
.total-row td {
  font-weight: bold;
  background: #c2d797;
}
.inline-task-form {
  background: #f0f4ff;
  border: 1px solid #c8d6ff;
  border-radius: 6px;
}
/* Modal */
.modal-backdrop {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
}
.modal-box {
  background: #fff;
  border-radius: 8px;
  padding: 24px;
  min-width: 320px;
  max-width: 400px;
  width: 100%;
  box-shadow: 0 4px 24px rgba(0,0,0,0.2);
}
</style>
